import React, { useState, useEffect } from 'react';
import { Link } from '@inertiajs/react';
import Helmet from 'react-helmet';
import { useMediaQuery } from '@mantine/hooks';
import { Tabs, Title, Text, MantineProvider, Button } from '@mantine/core';
import '../styles/LocalPageStyles.css';
// import '../styles/ScrollTabs.css';
import { useScrollableTabs } from '../src/hooks/useScrollableTabs';

interface Area {
  name: string;
  slug: string;
  strip_area_suffix: boolean;
  events_count: number;
  skyline_image_url: string;
}

interface Country {
  id: number;
  name: string;
  flag_emoji: string;
  areas: Area[];
}

interface Continent {
  name: string;
  countries: Country[];
}

interface LocalPageProps {
  continents: Continent[];
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
}

const LocalPage: React.FC<LocalPageProps> = ({ continents, page_meta }) => {
  const formatAreaName = (name: string, stripSuffix: boolean): string => {
    return stripSuffix ? name.replace(/\s*Area$/i, '') : name;
  };

  // Get default tab from first continent
  const defaultTab = continents?.[0]?.name || '';

  const { tabListReference } = useScrollableTabs();

  // State to track active tab/continent
  const [activeTab, setActiveTab] = useState(defaultTab);

  const isMobile = useMediaQuery('(max-width: 768px)');

  // Use a nested state structure for better organization
  const [expandedStates, setExpandedStates] = useState<Record<string, Record<string, boolean>>>({});

  // Toggle expansion state for a country with improved structure
  const toggleExpand = (continentName: string, countryName: string) => {
    setExpandedStates((prev) => {
      const newState = { ...prev };
      if (!newState[continentName]) {
        newState[continentName] = {};
      }
      newState[continentName][countryName] = !prev[continentName]?.[countryName];

      return newState;
    });
  };
  const DEFAULT_VISIBLE_CITIES = 12;

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }
    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  return (
    <div
      style={{
        backgroundColor: '#121212', // dark background
        color: '#f2f2f2', // text color
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif', // apply Poppins font
      }}
    >
      <MantineProvider>
        {page_meta && (
          <Helmet>
            <title>{page_meta.title}</title>
            <meta name="description" content={page_meta.description} />

            {/* Open Graph tags */}
            <meta property="og:title" content={page_meta.title} />
            <meta property="og:description" content={page_meta.description} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={page_meta.url} />
            <meta property="og:image" content={page_meta.image} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={page_meta.title} />
            <meta name="twitter:description" content={page_meta.description} />
            <meta name="twitter:image" content={page_meta.image} />
          </Helmet>
        )}
        <Tabs
          variant="none"
          color="orange"
          className="country-container"
          value={activeTab}
          onChange={setActiveTab}
          mb="2rem"
        >
          <Tabs.List
            ref={tabListReference}
            style={{
              padding: '30px',
              display: 'flex',
              justifyContent: isMobile ? 'flex-start' : 'center',
              backgroundColor: '#75757520',
            }}
          >
            {continents.map((continent) => (
              <Tabs.Tab key={continent.name} value={continent.name} style={{ color: '#f2f2f2', minHeight: '50px' }}>
                {continent.name}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {continents.map((continent) => (
            <Tabs.Panel key={continent.name} value={continent.name} pt="xs" mx="auto">
              {continent.countries
                .slice()
                .sort((a, b) => {
                  const aTotal = a.areas.reduce((sum, area) => sum + area.events_count, 0);
                  const bTotal = b.areas.reduce((sum, area) => sum + area.events_count, 0);
                  return bTotal - aTotal; // descending order
                })
                .map((country) => {
                  const sortedAreas = country.areas.slice().sort((a, b) => a.name.localeCompare(b.name));

                  // Determine if we need to show the "Show More" button
                  const hasMoreCities = sortedAreas.length > DEFAULT_VISIBLE_CITIES;

                  // Check if this country is expanded
                  const isExpanded = !!expandedStates[continent.name]?.[country.name];

                  // Get the cities to display based on expansion state
                  const visibleAreas = isExpanded ? sortedAreas : sortedAreas.slice(0, DEFAULT_VISIBLE_CITIES);

                  // Create a unique key for the country
                  const countryKey = `${continent.name}-${country.name}-${country.id || ''}`;

                  return (
                    <div key={countryKey} className="country-card" style={{ margin: '0 auto' }}>
                      <Title order={3} className="poppins" mb="0">
                        <span className="flag">{country.flag_emoji}</span>
                        {country.name}
                      </Title>

                      <Text size="sm" c="dimmed" mb="md">
                        {country.areas.length} {country.areas.length === 1 ? 'city' : 'cities & counting'}
                      </Text>

                      <ul className="areas-grid">
                        {visibleAreas.map((area) => (
                          <li key={area.slug} className="area-card-item">
                            <Link
                              href={`/city/${area.slug}`}
                              className="area-link"
                              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', width: '100%' }}
                            >
                              <div className="area-circle">
                                {area.skyline_image_url ? (
                                  <img
                                    src={area.skyline_image_url}
                                    alt={formatAreaName(area.name, area.strip_area_suffix)}
                                    className="area-skyline-image"
                                  />
                                ) : (
                                  formatAreaName(area.name, area.strip_area_suffix).charAt(0)
                                )}
                              </div>
                              <div className="area-info">
                                <span className="area-name">{formatAreaName(area.name, area.strip_area_suffix)}</span>
                                <span className="events-count">{area.events_count} events</span>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>

                      {hasMoreCities && (
                        <Button
                          variant="subtle"
                          color="orange"
                          size="lg"
                          onClick={() => toggleExpand(continent.name, country.name)}
                          fullWidth
                          mt="sm"
                          style={{
                            color: '#f2f2f2',
                            backgroundColor: '#EA580C',
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          {isExpanded
                            ? `Show Less (${DEFAULT_VISIBLE_CITIES} of ${sortedAreas.length})`
                            : `Show All ${sortedAreas.length} Cities (${sortedAreas.length - DEFAULT_VISIBLE_CITIES} more)`}
                        </Button>
                      )}
                    </div>
                  );
                })}
            </Tabs.Panel>
          ))}
        </Tabs>
      </MantineProvider>
    </div>
  );
};

export default LocalPage;
